import { Dialog, DialogContent, DialogDescription, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Formik } from 'formik';
import Text from 'components/Text';
import InputField from 'components/InputField';
import Dropdown from 'components/Dropdown';
import ErrorText from 'components/ErrorText';
import { fontSizes } from 'appconstants';
import { Audit } from 'types/audit_types';
import { audit_schema, audit_validation_schema } from 'validation_schema/audit_validation_schema';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { createAudit, editAudit } from 'store/actions/audit-action';

interface Props {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  audit?: Audit | null;
  onDone: () => void;
}

const AuditModal = ({ open, onClose, mode, audit, onDone }: Props) => {
  const dispatch: AppDispatch = useDispatch();

  const { user } = useSelector((state: RootState) => state.common);
  const { users } = useSelector((state: RootState) => state.user);
  const { customers } = useSelector((state: RootState) => state.customer);
  const { audit_loading } = useSelector((state: RootState) => state.audit);

  const isEdit = mode === 'edit';

  const onSubmit = (values: any, resetForm) => {
    const api_data = {
      auditName: values?.auditName,
      customerId: Number(values?.customerId),
      userIds: values?.users?.map((item: any) => Number(item?.id)) || [],
      auditStatusId: 2,
      storeId: null,
      distributionCenterId: null,
    };

    if (isEdit) {
      api_data['auditId'] = audit?.auditId;
      api_data['updatedBy'] = user?.userId;
    } else {
      api_data['createdBy'] = user?.userId;
    }

    if (isEdit) {
      dispatch(
        editAudit(api_data, () => {
          resetForm();
          onClose();
          onDone();
        }),
      );
    } else {
      dispatch(
        createAudit(api_data, () => {
          resetForm();
          onClose();
          onDone();
        }),
      );
    }
  };

  const getUser = (userId: number) => {
    const user = users?.items?.find((user) => user?.userId === userId)
    return { label: user?.fullName, id: String(user?.userId) };
  }

  const users_array = users?.items?.filter(val => val?.role?.toLowerCase() === 'auditor')


  return (
    <Dialog open={open} onOpenChange={audit_loading ? null : onClose}>
      <DialogContent className='max-w-[720px] max-h-[90%] overflow-auto rounded-xl'>
        <DialogTitle>
          <Text text={!isEdit ? 'Create Audit' : 'Edit Audit'} className='font-bold' size={fontSizes.h24} />
        </DialogTitle>
        <DialogDescription>
          <Text text={`Fill in the details to ${isEdit ? "edit an existing" : "create a new"} Audit.`} className='text-muted-foreground' />
        </DialogDescription>

        <Formik
          initialValues={
            isEdit ? { ...audit, users: audit?.users?.map((item) => getUser(item?.userId)) } : audit_schema
          }
          validationSchema={audit_validation_schema}
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={(values, functions) => {
            onSubmit(values, functions?.resetForm);
          }}
        >
          {({ values, errors, setFieldValue, setFieldError, handleSubmit }) => {
            return (
              <>
                <div className='grid grid-cols-1 gap-4 mt-2'>
                  <div>
                    <InputField
                      label='Audit Name'
                      placeholder='Enter Audit Name'
                      value={values.auditName}
                      onChange={(v) => {
                        setFieldValue('auditName', v);
                        setFieldError('auditName', '');
                      }}
                    />
                    {errors?.auditName && <ErrorText text={errors?.auditName} />}
                  </div>

                  <div>
                    <Dropdown
                      isSearchable
                      label='Customer'
                      placeholder='Select Customer'
                      value={customers?.items?.find((item) => item.customerId?.toString() === values.customerId?.toString())}
                      valueKey='customerName'
                      onChange={(v) => {
                        setFieldValue('customerId', v?.id ?? null);
                        setFieldError('customerId', null);
                      }}
                      options={customers?.items?.map((customer) => ({ label: customer.customerName, id: String(customer.customerId) })) || []}
                    />
                    {errors?.customerId && <ErrorText text={errors?.customerId} />}
                  </div>

                  <Text text='Assign Auditor' className='font-bold' size={fontSizes.h20} />

                  <div>
                    <Dropdown
                      label='Auditor'
                      isSearchable
                      placeholder='Select Auditor'
                      value={values?.users}
                      valueKey='label'
                      options={
                        users_array?.map((user) => ({
                          id: user?.userId?.toString(),
                          label: user?.fullName,
                        })) ?? []
                      }
                      onChange={(val) => {
                        if (val) {
                          const existingUsers = values?.users || [];

                          const alreadyExists = existingUsers?.some((item: any) => item?.id === val?.id);

                          const updatedUsers = alreadyExists
                            ? existingUsers?.filter((item: any) => item?.id !== val?.id) // remove
                            : [...existingUsers, val];
                          setFieldValue('users', updatedUsers);
                        } else setFieldValue('users', []);
                        setFieldError('users', '');
                      }}
                    />

                    {errors?.users && <ErrorText text={errors?.users?.toString()} />}
                  </div>
                </div>

                <div className='mt-4 flex gap-4'>
                  <Button variant='outline' className='h-12 w-full rounded-xl border-primary text-primary' onClick={onClose} disabled={audit_loading}>
                    Cancel
                  </Button>

                  <Button className='h-12 w-full rounded-xl' onClick={() => handleSubmit()} loading={audit_loading}>
                    {!isEdit ? 'Create' : 'Save'}
                  </Button>
                </div>
              </>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default AuditModal;
